import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, RefreshCw, X } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { get, put } from "@/lib/api";
import type { CompanyDetail, CompanyEmails, MailSyncStatus } from "@/lib/types";
import { addressChips, fetchingMail, syncBusy } from "./mail";

/** "E-Mail-Adressen": the company's own addresses (removable) and its contacts' (muted, "über Kontakt"). Only these bring mail in. */
export function EmailAddresses({ c }: { c: CompanyDetail }) {
  const qc = useQueryClient();
  const [value, setValue] = useState("");
  // While HEY works on queued addresses (their 12-month backfill) or a run, poll; once it is idle again the
  // company's timeline and last contact may have changed, so the detail is fetched again.
  const sync = useQuery({
    queryKey: ["mail-sync"],
    queryFn: () => get<MailSyncStatus>("/api/mail/sync"),
    refetchInterval: (query) => (syncBusy(query.state.data) ? 5000 : false),
  });
  const busy = syncBusy(sync.data);
  const chips = addressChips(c.emails, c.contacts);
  const fetching = fetchingMail(sync.data, chips.map((chip) => chip.email));
  const wasBusy = useRef(busy);
  useEffect(() => {
    if (wasBusy.current && !busy) {
      qc.invalidateQueries({ queryKey: ["company", c.id] });
      qc.invalidateQueries({ queryKey: ["companies"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    }
    wasBusy.current = busy;
  }, [busy, qc, c.id]);
  const save = useMutation({
    mutationFn: (emails: string[]) => put<CompanyEmails>(`/api/companies/${c.id}/emails`, { emails }),
    onSuccess: (_res, emails) => {
      qc.invalidateQueries({ queryKey: ["company", c.id] });
      qc.invalidateQueries({ queryKey: ["mail-sync"] });
      qc.invalidateQueries({ queryKey: ["companies"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      if (emails.length > c.emails.length && sync.data?.configured) toast("Mails der letzten 12 Monate werden geholt – das dauert etwa eine Minute.");
    },
    onError: (e) => toast.error(e.message),
  });
  const add = (e: FormEvent) => {
    e.preventDefault();
    const v = value.trim();
    if (v) save.mutate([...c.emails, v], { onSuccess: () => setValue("") });
  };

  return (
    <div className="flex flex-wrap items-center gap-2" aria-label="E-Mail-Adressen">
      <span className="text-sm text-muted-foreground">E-Mail-Adressen</span>
      {chips.map((chip) => chip.removable ? (
        <Badge key={chip.email} variant="secondary" className="gap-1 pr-1">
          {chip.email}
          <button type="button" aria-label={`Adresse ${chip.email} entfernen`} className="rounded-sm p-0.5 hover:bg-background"
            disabled={save.isPending} onClick={() => save.mutate(c.emails.filter((x) => x !== chip.email))}><X className="size-3" /></button>
        </Badge>
      ) : (
        <Badge key={chip.email} variant="outline" className="gap-1 font-normal text-muted-foreground" title={`über Kontakt ${chip.via}`}>
          {chip.email}<span className="text-xs">über Kontakt</span>
        </Badge>
      ))}
      <form onSubmit={add} className="flex items-center gap-1">
        <Input aria-label="E-Mail-Adresse hinzufügen" placeholder="name@firma.at" inputMode="email" autoComplete="off" spellCheck={false} data-1p-ignore
          className="h-8 w-48" value={value} onChange={(e) => setValue(e.target.value)} />
        <Button type="submit" variant="ghost" size="icon-sm" aria-label="E-Mail-Adresse hinzufügen" disabled={!value.trim() || save.isPending}><Plus /></Button>
      </form>
      {fetching && (
        <span role="status" className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <RefreshCw aria-hidden className="size-3.5 animate-spin motion-reduce:animate-none" />
          Mails der letzten 12 Monate werden geholt…
        </span>
      )}
    </div>
  );
}
